import * as React from 'react';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Calendar } from './Calendar';
import { FloatingLabel } from '../FloatingLabel';
import type {
  CalendarProps,
  DatePickerCalendarProps,
  DatePickerSingleOnSelect,
  DatePickerProps,
} from './types';

const defaultFormatDate = (date: Date) => format(date, 'PPP');
const defaultSerializeDate = (date: Date) => date.toISOString();

const splitCalendarProps = (
  props?: DatePickerCalendarProps,
): {
  calendarOnSelect?: DatePickerSingleOnSelect;
  restCalendarProps: Omit<CalendarProps, 'mode' | 'selected' | 'onSelect'>;
} => {
  if (!props) {
    return { calendarOnSelect: undefined, restCalendarProps: {} };
  }

  const { onSelect, ...rest } = props;
  return { calendarOnSelect: onSelect, restCalendarProps: rest };
};

const triggerBaseClasses = cn(
  'eye:body-m eye:inline-flex eye:min-w-[200px] eye:items-center eye:justify-between eye:gap-8',
  'eye:text-left eye:text-black eye:transition-colors',
  'eye:disabled:cursor-not-allowed eye:disabled:text-grey-100',
);

const focusClasses = cn(
  'eye:focus-visible:ring-2 eye:focus-visible:ring-black eye:focus-visible:ring-offset-2 eye:ring-offset-white',
  'eye:focus-visible:outline-none',
);

const standaloneButtonClasses = cn(
  'eye:h-[44px] eye:rounded-md eye:border eye:border-grey-100',
  'eye:px-12 eye:py-[10px]',
  'eye:hover:border-black eye:disabled:hover:border-grey-100',
);

export const DatePicker = ({
  date,
  defaultDate,
  onSelect,
  showCalendarIcon = true,
  placeholder = 'Select a date',
  disabled,
  required,
  closeOnSelect = true,
  formatDate = defaultFormatDate,
  serializeDate = defaultSerializeDate,
  calendarProps,
  labelProps,
  name,
  className,
  popoverClassName,
  id,
}: DatePickerProps) => {
  const [open, setOpen] = React.useState(false);
  const isControlled = date !== undefined;
  const [internalDate, setInternalDate] = React.useState<Date | undefined>(
    defaultDate,
  );
  const selectedDate = isControlled ? date : internalDate;

  const generatedId = React.useId();
  const generatedOrPropId = id ?? generatedId;

  React.useEffect(() => {
    if (!isControlled) {
      setInternalDate(defaultDate);
    }
  }, [defaultDate, isControlled]);

  const { calendarOnSelect, restCalendarProps } =
    splitCalendarProps(calendarProps);

  const handleSelect: DatePickerSingleOnSelect = (
    nextDate,
    triggerDate,
    modifiers,
    event,
  ) => {
    calendarOnSelect?.(nextDate, triggerDate, modifiers, event);
    if (!isControlled) {
      setInternalDate(nextDate);
    }
    onSelect?.(nextDate);
    if (closeOnSelect && nextDate) {
      setOpen(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!disabled) {
      setOpen(nextOpen);
    }
  };

  const isPlaceholder = !selectedDate;
  const displayValue = selectedDate ? formatDate(selectedDate) : placeholder;
  const hiddenValue = selectedDate ? serializeDate(selectedDate) : '';

  const resolvedLabelProps = labelProps
    ? {
        ...labelProps,
        htmlFor: generatedOrPropId,
        className: cn(labelProps.className, 'eye:min-w-200'),
        required: required ?? labelProps.required,
        disabled: disabled ?? labelProps.disabled,
      }
    : undefined;

  const triggerButton = (
    <button
      id={generatedOrPropId}
      type='button'
      aria-label={isPlaceholder ? placeholder : displayValue}
      aria-haspopup='dialog'
      aria-expanded={disabled ? undefined : open}
      disabled={disabled}
      className={cn(
        'eye:cursor-pointer',
        className,
        triggerBaseClasses,
        resolvedLabelProps
          ? 'eye:focus-visible:outline-none'
          : [standaloneButtonClasses, focusClasses],
      )}
    >
      <span
        className={cn(
          'eye:min-w-0 eye:flex-1 eye:truncate',
          isPlaceholder ? 'eye:text-grey-placeholder' : 'eye:text-black',
        )}
      >
        {displayValue}
      </span>
      {showCalendarIcon && (
        <CalendarIcon
          className={cn('eye:shrink-0', disabled && 'eye:text-grey-disabled')}
          aria-hidden='true'
        />
      )}
    </button>
  );

  const triggerChild = resolvedLabelProps ? (
    <FloatingLabel {...resolvedLabelProps}>{triggerButton}</FloatingLabel>
  ) : (
    triggerButton
  );

  return (
    <>
      <Popover open={!disabled && open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>{triggerChild}</PopoverTrigger>
        <PopoverContent
          align='start'
          className={cn('eye:w-auto', popoverClassName)}
        >
          <Calendar
            mode='single'
            defaultMonth={selectedDate}
            selected={selectedDate}
            onSelect={handleSelect}
            {...restCalendarProps}
          />
        </PopoverContent>
      </Popover>
      <input
        type='hidden'
        name={name}
        value={hiddenValue}
        disabled={disabled}
      />
    </>
  );
};
